import { useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { LoadingBootScreen } from "../loading/LoadingBootScreen";
import { supabase } from "../../lib/supabase";
import {
  buildDesktopDeepLink,
  isDesktopAuthBridgePath,
  markDesktopAuthRestoreIntent,
} from "../../lib/desktop-auth-restore";

type BridgeStatus = "restoring" | "redirecting" | "opened" | "error";

function readHashParams(): URLSearchParams {
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  return new URLSearchParams(hash);
}

function readSafeDesktopAuthUrl(): string | null {
  const raw = new URLSearchParams(window.location.search).get("authUrl");
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (
      url.protocol === "https:" &&
      url.hostname.endsWith(".supabase.co") &&
      url.pathname.includes("/auth/v1/authorize")
    ) {
      return url.toString();
    }
  } catch {
    /* ignore */
  }
  return null;
}

function openDesktop(deepLink: string): void {
  window.location.replace(deepLink);
}

async function storeSessionForDesktopPolling(session: Session): Promise<void> {
  if (!session.access_token || !session.refresh_token) return;
  const body = {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_in: session.expires_in ? String(session.expires_in) : undefined,
    token_type: session.token_type || "bearer",
  };
  try {
    await fetch("/api/auth/supabase/store", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    /* Deep link remains the primary handoff when polling store is unavailable. */
  }
}

async function restoreCurrentWebSession(): Promise<string | null> {
  if (!supabase) return null;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const firstLink = session ? buildDesktopDeepLink(session) : null;
  if (firstLink && session) {
    await storeSessionForDesktopPolling(session);
    return firstLink;
  }

  const { data } = await supabase.auth.refreshSession();
  const refreshedLink = data.session
    ? buildDesktopDeepLink(data.session)
    : null;
  if (refreshedLink && data.session) {
    await storeSessionForDesktopPolling(data.session);
  }
  return refreshedLink;
}

async function consumeWebCallback(): Promise<string | null> {
  if (!supabase) return null;
  const query = new URLSearchParams(window.location.search);
  const hash = readHashParams();
  const accessToken = hash.get("access_token") || query.get("access_token");
  const refreshToken = hash.get("refresh_token") || query.get("refresh_token");
  const code = query.get("code");

  if (accessToken && refreshToken) {
    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) throw error;
    if (data.session) await storeSessionForDesktopPolling(data.session);
    return data.session ? buildDesktopDeepLink(data.session) : null;
  }

  if (code) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw error;
    if (data.session) await storeSessionForDesktopPolling(data.session);
    return data.session ? buildDesktopDeepLink(data.session) : null;
  }

  return null;
}

async function startChromeOAuthFallback(): Promise<void> {
  if (!supabase) throw new Error("Supabase is not configured.");
  const redirectTo = new URL(
    "/auth/desktop/callback",
    window.location.origin,
  ).toString();
  markDesktopAuthRestoreIntent("desktop-oauth");
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo,
      skipBrowserRedirect: true,
    },
  });
  if (error) throw error;
  if (!data.url) throw new Error("Google sign-in did not return a URL.");
  window.location.replace(data.url);
}

function DesktopAuthBridgeView() {
  const [status, setStatus] = useState<BridgeStatus>("restoring");
  const [manualLink, setManualLink] = useState<string | null>(null);
  const [message, setMessage] = useState("Restoring Desktop session");
  const desktopAuthUrl = useMemo(readSafeDesktopAuthUrl, []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        if (!supabase) throw new Error("Supabase is not configured.");

        const callbackLink = await consumeWebCallback();
        if (cancelled) return;
        if (callbackLink) {
          setStatus("opened");
          setManualLink(callbackLink);
          setMessage("Opening Fintheon Desktop");
          openDesktop(callbackLink);
          return;
        }

        const sessionLink = await restoreCurrentWebSession();
        if (cancelled) return;
        if (sessionLink) {
          setStatus("opened");
          setManualLink(sessionLink);
          setMessage("Opening Fintheon Desktop");
          openDesktop(sessionLink);
          return;
        }

        if (desktopAuthUrl) {
          setStatus("redirecting");
          setMessage("Opening Google sign-in");
          markDesktopAuthRestoreIntent("desktop-auth-url");
          window.location.replace(desktopAuthUrl);
          return;
        }

        setStatus("redirecting");
        setMessage("Opening Google sign-in");
        await startChromeOAuthFallback();
      } catch (error) {
        if (cancelled) return;
        setStatus("error");
        setMessage(
          error instanceof Error
            ? error.message
            : "Unable to restore the Desktop session.",
        );
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [desktopAuthUrl]);

  return (
    <div className="desktop-auth-bridge">
      <LoadingBootScreen phrase={message} />
      <div className="desktop-auth-bridge__fallback" aria-live="polite">
        {status === "opened" && manualLink ? (
          <a href={manualLink}>Open Fintheon Desktop</a>
        ) : null}
        {status === "error" ? <p>{message}</p> : null}
      </div>
      <style>{`
        .desktop-auth-bridge__fallback {
          position: fixed;
          left: 50%;
          bottom: 2rem;
          z-index: 10;
          transform: translateX(-50%);
          color: rgba(240, 234, 214, 0.68);
          font: 600 0.78rem/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          text-align: center;
        }

        .desktop-auth-bridge__fallback a {
          color: #c79f4a;
          text-decoration: none;
        }
      `}</style>
    </div>
  );
}

export const DesktopAuthBridge = Object.assign(DesktopAuthBridgeView, {
  shouldHandleCurrentPath: isDesktopAuthBridgePath,
});
